// ─────────────────────────────────────────────────────────────────
// Daily Points Tracker (Feb 2026)
// ─────────────────────────────────────────────────────────────────
// Aggregates every point a child earns across the app on a given
// calendar day (device local time). Resets automatically at midnight.
// Fires milestone celebrations at 25 / 50 / 100 / 200 points and
// stores an end-of-day recap the next login screen can surface.
//
//   • addDailyPoints(n, source)   — call from any game/quiz/quest end.
//                                    Returns { total, milestone|null }.
//   • getDailyState()             — read today's total + hit milestones.
//   • getYesterdayRecap()         — { total, activities, streak } or null.
//   • getDailyStreak()            — consecutive days ≥25 pts.
//
// Storage keys (localStorage, no PII):
//   mwq_daily_today   → { date: "YYYY-MM-DD", total, hits: {25,50,100,200},
//                          activities: [{ source, points, at }] }
//   mwq_daily_recap   → previous day's snapshot (survives one day for
//                        the recap card on next login)
//   mwq_daily_streak  → consecutive days ≥ 25 pts

const TODAY_KEY  = "mwq_daily_today";
const RECAP_KEY  = "mwq_daily_recap";
const STREAK_KEY = "mwq_daily_streak";
export const MILESTONES = [25, 50, 100, 200];

// Milestone name/emoji copy — kids should feel each one is a real thing.
export const MILESTONE_LABEL = {
  25:  { emoji: "⭐",  title: "Daily Star",   blurb: "You earned 25 points today!" },
  50:  { emoji: "🚀", title: "Super Learner", blurb: "50 points and counting!"     },
  100: { emoji: "🏆", title: "Champion",      blurb: "100 points! You're on fire!" },
  200: { emoji: "👑", title: "Legend",        blurb: "200 points! Legend status."   },
};

// Local YYYY-MM-DD so a device in Sydney doesn't share midnight with UTC.
function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function readJSON(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; }
  catch { return fallback; }
}
function writeJSON(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

// Rotate the state at midnight — moves today into recap, resets today,
// updates the daily-25 streak. Safe to call at any time; only runs when
// the stored date differs from today's.
function rotateIfNeeded() {
  const cur = readJSON(TODAY_KEY, null);
  const today = todayKey();
  if (!cur) {
    writeJSON(TODAY_KEY, { date: today, total: 0, hits: {}, activities: [] });
    return;
  }
  if (cur.date === today) return;
  // Archive yesterday as the recap. Bump/reset the ≥25 streak.
  writeJSON(RECAP_KEY, cur);
  const prevStreak = parseInt(localStorage.getItem(STREAK_KEY) || "0", 10) || 0;
  const nextStreak = (cur.total >= 25) ? (prevStreak + 1) : 0;
  try { localStorage.setItem(STREAK_KEY, String(nextStreak)); } catch { /* ignore */ }
  writeJSON(TODAY_KEY, { date: today, total: 0, hits: {}, activities: [] });
}

// Public API — every point-earning call site funnels into this.
export function addDailyPoints(n, source = "") {
  const amount = Math.max(0, Math.round(n));
  if (!amount) return { total: 0, milestone: null };
  rotateIfNeeded();
  const cur = readJSON(TODAY_KEY, { date: todayKey(), total: 0, hits: {}, activities: [] });
  const prev = cur.total || 0;
  cur.total = prev + amount;
  cur.activities = [
    { source: String(source).slice(0, 60), points: amount, at: Date.now() },
    ...(cur.activities || []),
  ].slice(0, 40);
  // Which (if any) milestone did we JUST cross? Only the biggest one
  // that fires this call — kids shouldn't get 4 popups from one +200.
  let milestone = null;
  for (const m of MILESTONES) {
    if (prev < m && cur.total >= m && !cur.hits[m]) {
      cur.hits[m] = Date.now();
      milestone = m;
    }
  }
  writeJSON(TODAY_KEY, cur);
  // Broadcast so any mounted UI (milestone banner, HUD) can react.
  try {
    window.dispatchEvent(new CustomEvent("mwq-daily-points", { detail: { total: cur.total, milestone, source } }));
  } catch { /* ignore */ }
  return { total: cur.total, milestone };
}

export function getDailyState() {
  rotateIfNeeded();
  const s = readJSON(TODAY_KEY, { date: todayKey(), total: 0, hits: {}, activities: [] });
  return {
    date:     s.date,
    total:    s.total || 0,
    hits:     s.hits || {},
    activities: s.activities || [],
    next:     nextMilestone(s.total || 0),
  };
}

export function nextMilestone(total = 0) {
  for (const m of MILESTONES) if (total < m) return m;
  return null;
}

export function getYesterdayRecap() {
  rotateIfNeeded();
  return readJSON(RECAP_KEY, null);
}

export function clearYesterdayRecap() {
  try { localStorage.removeItem(RECAP_KEY); } catch { /* ignore */ }
}

export function getDailyStreak() {
  try { return parseInt(localStorage.getItem(STREAK_KEY) || "0", 10) || 0; }
  catch { return 0; }
}
